"use client";

import { useEffect, useCallback, useState, useRef } from "react";
import { useLocaleRouter } from "@/lib/hooks/useLocaleRouter";
import { useSearchParams } from "next/navigation";
import { toast } from "sonner";
import { useAuthStore } from "@/lib/store";
import { useLocaleStore, useTranslation } from "@/lib/hooks/useTranslation";
import { anonSessionHeader } from "@/lib/anonSession";
import { trackLogin, trackSignUp, trackFunnelStep, trackConversion } from "@/lib/analytics";
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: {
              credential: string;
            }) => void;
            auto_select?: boolean;
          }) => void;
          renderButton: (element: HTMLElement, options: {
            theme?: "outline" | "filled_blue" | "filled_black";
            size?: "large" | "medium" | "small";
            text?: "signin_with" | "signup_with" | "continue_with";
            shape?: "rectangular" | "pill" | "circle" | "square";
            logo_alignment?: "left" | "center";
            width?: number;
            locale?: string;
          }) => void;
        };
      };
    };
  }
}
const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || "";
export function GoogleSignInButton() {
  const {
    locale
  } = useTranslation();
  const router = useLocaleRouter();
  const searchParams = useSearchParams();
  const {
    setAuth
  } = useAuthStore();
  const {
    setLocale
  } = useLocaleStore();
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Post-Google-signin destination. Matches login/register priority:
  //  1. `?from=ats` → /resumes (picks up pendingATSAnalysis)
  //  2. `?redirect_to=/safe-path` (same-origin relative only)
  //  3. /dashboard (default)
  const rawRedirect = searchParams?.get("redirect_to") ?? "";
  const safeRedirect = rawRedirect.startsWith("/") && !rawRedirect.startsWith("//") ? rawRedirect : null;
  const fromParam = searchParams?.get("from");
  const redirectTo = fromParam === "ats" ? "/resumes" : safeRedirect ?? "/dashboard";
  const handleGoogleCallback = useCallback(async (response: {
    credential: string;
  }) => {
    try {
      const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

      // Send token to backend. X-Anon-Session (localStorage) is the primary
      // ownership signal so the backend can claim the guest's pre-signup ATS
      // analysis; credentials:"include" keeps the legacy velyq_anon_id cookie
      // as a fallback. Without one of these, Google signup from the ATS gate
      // loses the CV.
      const res = await fetch(`${API_URL}/api/v1/auth/google`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...anonSessionHeader()
        },
        credentials: "include",
        body: JSON.stringify({
          credential: response.credential
        })
      });
      if (!res.ok) {
        const error_0 = await res.json();
        throw new Error(error_0.detail || "Google authentication failed");
      }
      const tokens = await res.json();

      // Get user info — guard against a broken /users/me response so we
      // don't write garbage to the auth store.
      const userRes = await fetch(`${API_URL}/api/v1/users/me`, {
        headers: {
          Authorization: `Bearer ${tokens.access_token}`
        }
      });
      if (!userRes.ok) {
        throw new Error(`users/me failed: ${userRes.status}`);
      }
      const user = await userRes.json();

      // Store auth state
      setAuth(user, tokens.access_token, tokens.refresh_token);

      // Set locale
      if (user.ui_language) {
        setLocale(user.ui_language);
      }

      // Track Google sign-in (could be login or signup)
      trackLogin('google');
      if (user.created_at && new Date(user.created_at).getTime() > Date.now() - 60000) {
        // User was just created (within last minute) - it's a signup
        trackSignUp('google');
        trackFunnelStep('register_complete');
        trackConversion('registration');
      }
      toast.success(locale === "fr" ? "Connexion réussie" : "Login successful");
      // Land directly on the just-claimed guest ATS analysis when present,
      // otherwise fall back to the normal post-login destination.
      if (tokens.analysis_id && tokens.resume_id) {
        router.push(`/resumes/${tokens.resume_id}/ats/${tokens.analysis_id}`);
      } else {
        router.push(redirectTo);
      }
    } catch (error) {
      console.error("Google auth error:", error);
      toast.error(locale === "fr" ? "Erreur de connexion avec Google" : "Google sign-in failed");
    }
  }, [router, setAuth, setLocale, locale, redirectTo]);
  useEffect(() => {
    // Load Google Identity Services script
    if (typeof window !== "undefined" && !window.google) {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => setIsScriptLoaded(true);
      document.body.appendChild(script);
    } else if (window.google) {
      setIsScriptLoaded(true);
    }
  }, []);
  useEffect(() => {
    if (isScriptLoaded && window.google && GOOGLE_CLIENT_ID && buttonRef.current) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCallback
      });

      // Render the official Google button - responsive width
      const buttonWidth = Math.min(buttonRef.current.offsetWidth || 300, 400);
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: "filled_black",
        size: "large",
        text: "continue_with",
        shape: "rectangular",
        width: buttonWidth,
        locale: locale === "fr" ? "fr" : "en"
      });
    }
  }, [isScriptLoaded, handleGoogleCallback, locale]);
  if (!GOOGLE_CLIENT_ID) {
    return null;
  }
  return <div className="flex w-full justify-center">
      <div ref={buttonRef} className="w-full" />
    </div>;
}